/**
 * The clinic dashboard.
 *
 * It used to be four tiles of clinic-wide quota, two of which - the plan name and
 * the period end date - are the same figure for every store on every day of the
 * month, and neither of which anybody opens the app to look at.
 *
 * What this answers instead is what somebody actually arrives wanting to know:
 *
 *   Is anything being written, and where. Per store, because that is the only
 *   figure that differs per store - a clinic-wide number repeated four times is
 *   four tiles saying one thing.
 *
 *   Where did the article I just made go. It is in the list now, which it was
 *   not: the panel said "no articles yet" regardless of how many there were.
 *
 *   Is anything stopping me. A clinic with no approved treatment cannot generate
 *   a body at all, and discovering that from a failed generation is discovering
 *   it too late.
 *
 * The store selector in the status strip decides the depth, and since migration
 * 021 it decides the *meaning* of the allowance too: articles and images are each
 * store's own quota, so
 *
 *   one store selected  - the tiles are that store's remaining articles and
 *                         images, which is a figure its staff can act on.
 *   all stores          - there is no single remaining figure to show, because
 *                         there are several. The tiles become an overview: how
 *                         many stores, how many are running out, and the month's
 *                         output across them. The per-store table below carries
 *                         the individual numbers.
 *
 * What is deliberately absent is the AI spend. It is still clinic-wide and still
 * the ceiling that stops a runaway - see quotas/service.ts - but it is a vendor
 * cost against a contract JMC holds, denominated in a currency the clinic is not
 * billed in. Nothing a clinic can do about it and nothing it should have to read;
 * it belongs on the JMC screens, which have it.
 */
import { Link, useLoaderData, useOutletContext } from 'react-router';
import type { DashboardData, QuotaLine, Session, UsageSummary } from '../api.ts';
import { Card, Empty, Meter, Pill, Stat, StatusPill, Table, jaDate, jaNumber } from '../components/ui.tsx';
import { SkeletonRows, SkeletonTiles, usePendingScreen } from '../components/Skeleton.tsx';
import { StoreTile, type StoreMetric } from '../components/StoreTile.tsx';
import { usePaged } from '../paging.ts';

/** How many articles the panel shows before it stops and offers the full list. */
const RECENT_SHOWN = 5;

/**
 * Store rows the loader ships, and the size of every page after it.
 *
 * Must match the server's own first page - `usePaged` reads a short first page
 * as "there is no more", so a client asking for a different number would either
 * stop early or ask for a page that is already on screen.
 */
const STORE_PAGE = 10;

/*
 * The tables behind the store list. A module constant, not an inline array: a
 * new array on every render would look to `usePaged` like a different list and reset
 * the pages already loaded.
 */
const STORE_TABLES = ['stores', 'articles', 'clinic_usage_periods'] as const;

const READINESS_LABEL: Record<string, string> = {
  personas: 'ペルソナ',
  treatments: '施術・コース・料金',
  supervisors: '監修医',
  cta_templates: 'CTAテンプレート',
  strengths: 'クリニックの強み',
};

/** Material a body generation cannot proceed without; see ai/generate.ts REQUIRED. */
const BLOCKING = new Set(['personas', 'treatments']);

/**
 * One allowance: what is left, how much of the limit that is, and a meter.
 *
 * The value shown is what *remains*, because the question this answers is "can I
 * still publish today" - used-so-far is the same information phrased as
 * something nobody has to act on.
 *
 * The meter never carries the state alone. Its colour moves with severity, and
 * the pill beside it says the same thing in words and figures, which is what a
 * colour-blind reader, a grayscale print and forced-colors mode all fall back to.
 */
function Allowance({ label, line }: { label: string; line: QuotaLine | undefined }) {
  if (!line) return <Stat label={label} value="—" />;
  const { used, limit, remaining } = line;
  const ratio = limit ? used / limit : 0;
  const tone = limit === null ? 'muted' : ratio >= 1 ? 'danger' : ratio >= 0.8 ? 'warn' : 'ok';
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value">{limit === null ? jaNumber(used) : jaNumber(remaining)}</div>
      <div style={{ marginTop: 2 }}>
        <Pill tone={tone}>
          {limit === null ? `${jaNumber(used)} 使用（上限なし）` : `${used} / ${limit} 使用`}
        </Pill>
      </div>
      <Meter used={used} limit={limit} label={`${label}の使用状況`} />
    </div>
  );
}

interface FactRow {
  kind: string;
  approved: number;
  total: number;
}

/**
 * One scope's approved material.
 *
 * `blocking` is decided by the caller rather than here, because whether a
 * missing treatment actually blocks a store depends on something this component
 * cannot see: a store that draws on the clinic's material is not blocked by
 * having none of its own.
 */
function FactScope({
  title,
  note,
  rows,
  blocking,
}: {
  title: string;
  note?: string;
  rows: FactRow[];
  blocking: string[];
}) {
  return (
    <section className="scopegroup">
      <p className="scopegroup__head">
        <span className="scopegroup__title">{title}</span>
        {note && <span className="scopegroup__note">{note}</span>}
      </p>

      {blocking.length > 0 && (
        <p className="alert" role="status">
          {blocking.map((kind) => READINESS_LABEL[kind] ?? kind).join('と')}
          に承認済みのものがないため、本文の生成はまだできません。
        </p>
      )}

      <div className="scopegroup__rows">
        {rows.map((row) => (
          <div className="scoperow" key={row.kind}>
            <span className="scoperow__label">{READINESS_LABEL[row.kind] ?? row.kind}</span>
            <span className="scoperow__count">
              {row.approved === 0 && BLOCKING.has(row.kind) ? (
                <Pill tone="danger">承認 0</Pill>
              ) : (
                <>
                  承認 <strong>{jaNumber(row.approved)}</strong>
                  <span className="muted"> / 登録 {jaNumber(row.total)}</span>
                </>
              )}
            </span>
            <Link
              className="btn btn--sm btn--ghost"
              to={row.kind === 'personas' ? '/personas' : `/catalog/${row.kind.replace('_', '-')}`}
            >
              管理
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * One store's article activity.
 *
 * The month's count leads, because the question is "is this store being kept
 * up", and a lifetime total answers that only for a store nobody has touched in
 * a year. Drafts sit beside it: a store with eight drafts and nothing published
 * is a different problem from a store with nothing at all.
 */
function StoreRow({ store }: { store: DashboardData['stores'][number] }) {
  const total = Number(store.total);
  return (
    <tr className="row-link">
      <td>
        <Link className="row-link__target" to={`/stores/${store.id}`}>
          {store.name}
        </Link>
      </td>
      <td className="num">{jaNumber(store.this_month)}</td>
      <td className="num">{jaNumber(total)}</td>
      <td className="num">
        {/*
          The store's own allowance. `storeQuotas` returns a line for every
          non-archived store whether or not it has a period row yet - a store
          that has written nothing still has the plan's limit - so the null arm
          here is defensive rather than a state anybody sees.
        */}
        {store.articlesQuota === null ? (
          <span className="muted">—</span>
        ) : store.articlesQuota.limit === null ? (
          <span className="muted">上限なし</span>
        ) : (
          <Pill tone={store.articlesQuota.remaining === 0 ? 'danger' : 'muted'}>
            {jaNumber(store.articlesQuota.remaining)} / {jaNumber(store.articlesQuota.limit)}
          </Pill>
        )}
      </td>
      <td>
        {total === 0 ? (
          <Pill tone="warn">記事なし</Pill>
        ) : (
          <span className="dashcounts">
            {Number(store.draft) > 0 && <Pill tone="muted">下書き {store.draft}</Pill>}
            {Number(store.in_review) > 0 && <Pill tone="warn">確認中 {store.in_review}</Pill>}
            {Number(store.approved) > 0 && <Pill tone="ok">承認 {store.approved}</Pill>}
            {Number(store.published) > 0 && <Pill>公開 {store.published}</Pill>}
          </span>
        )}
      </td>
      <td>{jaDate(store.last_article_at)}</td>
    </tr>
  );
}

export default function ClinicDashboard() {
  const { usage, dashboard } = useLoaderData<{
    usage: UsageSummary | null;
    dashboard: DashboardData | null;
  }>();
  const session = useOutletContext<Session>();
  const clinic = session.memberships.find((m) => m.clinicId === session.activeClinicId);
  const store = clinic?.stores.find((s) => s.id === session.activeStoreId);
  const pending = usePendingScreen();

  const isClinicAdmin = clinic?.clinicAdmin ?? false;
  /*
   * The store rows, growing as the reader scrolls.
   *
   * Cached and invalidated by the same tables the loader uses, so scrolling back
   * down the list costs nothing and a new article still refreshes the row it
   * belongs to.
   */
  const storeList = usePaged<DashboardData['stores'][number]>(
    '/dashboard/stores',
    dashboard?.stores ?? [],
    STORE_PAGE,
    true,
    STORE_TABLES,
  );
  const stores = storeList.items;
  const recent = dashboard?.recent ?? [];
  const readiness = dashboard?.readiness ?? [];

  /*
   * Totals from the per-store rows rather than a second query. They are already
   * here, they already agree with the table below them, and a separately
   * computed total that disagrees with its own breakdown is worse than no total.
   */
  const thisMonth = stores.reduce((sum, s) => sum + Number(s.this_month), 0);
  const totalArticles = stores.reduce((sum, s) => sum + Number(s.total), 0);
  /*
   * Whether the clinic's own cap is the one that will bite.
   *
   * Two ceilings apply at once - each store's allowance and the clinic total -
   * and a plan can be sold where the stores add up to more than the clinic is
   * allowed. When that is so, every store shows room left right up to the moment
   * generation is refused, and nothing on this screen explained why. null when
   * either side is uncapped, because then there is nothing to compare.
   */
  const clinicCapBinds = (() => {
    const cap = usage?.clinicArticles.limit ?? null;
    if (cap === null || stores.length === 0) return null;
    if (stores.some((row) => (row.articlesQuota?.limit ?? null) === null)) return null;
    const perStore = stores.reduce((sum, row) => sum + (row.articlesQuota?.limit ?? 0), 0);
    return perStore > cap;
  })();

  const articlesUsed = stores.reduce((sum, s) => sum + (s.articlesQuota?.used ?? 0), 0);
  const imagesUsed = stores.reduce((sum, s) => sum + (s.imagesQuota?.used ?? 0), 0);

  /**
   * One store row turned into a tile row.
   *
   * A helper rather than four nearly identical maps: the four tiles differ only
   * in which number they read and what it is measured against, and writing that
   * out four times is four places for the store name to be taken from somewhere
   * else.
   */
  const metric = (
    value: (row: DashboardData['stores'][number]) => number,
    limit: (row: DashboardData['stores'][number]) => number | null,
  ): StoreMetric[] =>
    stores.map((row) => ({
      storeId: row.id,
      name: row.name,
      value: value(row),
      limit: limit(row),
    }));
  /*
   * Readiness, grouped the way the person looking at it thinks about it.
   *
   * A clinic admin sees the shared material and then each store's, because they
   * own both and the difference is what they are deciding about. A store user
   * receives exactly two groups from the server - the clinic's and their own -
   * because RLS never sends them a sibling's row.
   *
   * Whether a group is *blocked* is not the same question as whether it is
   * empty. A store that draws on the clinic's material can generate on the
   * strength of the clinic's approved treatment; a store that has opted out
   * cannot, and that is the case worth warning about.
   */
  const factScopes = new Map(
    (dashboard?.factScopes ?? []).map((row) => [row.store_id, row.use_clinic_facts]),
  );

  const clinicRows: FactRow[] = readiness
    .filter((row) => row.store_id === null)
    .map((row) => ({ kind: row.kind, approved: Number(row.approved), total: Number(row.total) }));

  const clinicApproved = new Set(clinicRows.filter((row) => row.approved > 0).map((r) => r.kind));

  const storeIds = [...new Set(readiness.map((row) => row.store_id).filter((id) => id !== null))];

  const groups = [
    {
      storeId: null as string | null,
      title: 'クリニック共通',
      note: '全店舗の記事で使われます',
      rows: clinicRows,
      // Never blocking on its own: a store with its own material does not need
      // the clinic to have any.
      blocking: [] as string[],
    },
    ...storeIds.map((storeId) => {
      const rows = readiness
        .filter((row) => row.store_id === storeId)
        .map((row) => ({
          kind: row.kind,
          approved: Number(row.approved),
          total: Number(row.total),
        }));
      const usesClinic = factScopes.get(storeId) ?? true;
      const own = new Set(rows.filter((row) => row.approved > 0).map((row) => row.kind));
      return {
        storeId,
        title: readiness.find((row) => row.store_id === storeId)?.store_name ?? '店舗',
        note: usesClinic ? 'クリニック共通＋この店舗の情報' : 'この店舗の情報のみ（共通は使わない設定）',
        rows,
        blocking: [...BLOCKING].filter(
          (kind) => !own.has(kind) && !(usesClinic && clinicApproved.has(kind)),
        ),
      };
    }),
  ];

  if (!session.activeClinicId) {
    return (
      <Card title="ダッシュボード">
        <Empty>クリニックを選択すると、今期の状況が表示されます。</Empty>
      </Card>
    );
  }

  return (
    <>
      <Card
        title={store ? `${store.name}の状況` : (clinic?.clinicName ?? '今期の状況')}
        caption={
          store
            ? '店舗を「全店舗」に戻すと、クリニック全体の比較が表示されます。'
            : 'クリニック全体の今期（契約期間）の状況です。店舗を選ぶと、その店舗の詳細になります。'
        }
        actions={
          <Link className="btn btn--sm" to="/articles/new">
            新規記事を作成
          </Link>
        }
      >
        {pending ? (
          <SkeletonTiles />
        ) : (
          <div className="grid grid--4">
            {store ? (
              <>
                {/* One store selected: its own allowance, which its staff can act on. */}
                <Allowance label="この店舗の残り記事数" line={usage?.articles} />
                <Allowance label="この店舗の残り画像数" line={usage?.images} />
                <Stat label="今期の作成数" value={jaNumber(thisMonth)} small />
                <Stat label="この店舗の記事" value={jaNumber(totalArticles)} small />
              </>
            ) : (
              <>
                {/*
                  No single store selected, so no single figure. Each tile is the
                  same four numbers broken down by store, because with the
                  allowance per store that is where the number lives - a clinic
                  total would be a figure nobody can spend.
                */}
                <StoreTile
                  label="記事の使用（今期・店舗別）"
                  total={jaNumber(articlesUsed)}
                  unit="件"
                  metrics={metric((row) => row.articlesQuota?.used ?? 0, (row) => row.articlesQuota?.limit ?? null)}
                />
                <StoreTile
                  label="画像の使用（今期・店舗別）"
                  total={jaNumber(imagesUsed)}
                  unit="枚"
                  metrics={metric((row) => row.imagesQuota?.used ?? 0, (row) => row.imagesQuota?.limit ?? null)}
                />
                <StoreTile
                  label="今期の作成数（店舗別）"
                  total={jaNumber(thisMonth)}
                  unit="件"
                  // No limit: the bar is each store's share of the month, which
                  // is the only ratio an uncapped count has.
                  metrics={metric((row) => Number(row.this_month), () => null)}
                />
                <StoreTile
                  label="記事の総数（全期間・店舗別）"
                  total={jaNumber(totalArticles)}
                  unit="件"
                  metrics={metric((row) => Number(row.total), () => null)}
                />
              </>
            )}
          </div>
        )}

        {/*
          Where the plan and the period went. One line, not two tiles.

          Hidden mid-switch along with everything else: the plan belongs to the
          clinic, so on a clinic change it is one of the figures that would
          otherwise be shown as the new clinic's.
        */}
        {/*
          The clinic's own ceiling, where the plan sets one.

          The tiles above are per store, which is the number a store can act on -
          but a clinic on a plan with a total can exhaust it while every single
          store still shows room left. Shown only when a cap exists: a line
          reading 「無制限」 is a line nobody needs to read twice.
        */}
        {!pending && usage && (usage.clinicArticles.limit !== null || usage.clinicImages.limit !== null) && (
          <p className="muted small">
            クリニック合計（今期・全店舗の合算）
            {usage.clinicArticles.limit !== null && (
              <>
                <span aria-hidden> · </span>
                記事{' '}
                <strong>
                  {jaNumber(usage.clinicArticles.used)} / {jaNumber(usage.clinicArticles.limit)}
                </strong>
              </>
            )}
            {usage.clinicImages.limit !== null && (
              <>
                <span aria-hidden> · </span>
                画像{' '}
                <strong>
                  {jaNumber(usage.clinicImages.used)} / {jaNumber(usage.clinicImages.limit)}
                </strong>
              </>
            )}
            {/*
              Which ceiling actually stops a generation.
              
              The tiles above are per store and this line is the clinic, and the
              two are different numbers on purpose - but the screen never said
              which one binds. On the current plan the stores add up to more than
              the clinic is allowed, so every store can show room left while the
              next article is refused. That is the sentence somebody needs.
            */}
            {clinicCapBinds !== null && (
              <span style={{ display: 'block', marginTop: 4 }}>
                {clinicCapBinds
                  ? '店舗ごとの残数を合計すると、このクリニック上限を超えます。先に上限に達するのはクリニック合計の方です。'
                  : '各店舗の上限の合計が、このクリニック上限に収まっています。先に上限に達するのは店舗ごとの残数です。'}
              </span>
            )}
          </p>
        )}

        <p className="muted small dashplan" hidden={pending}>
          契約プラン <strong>{usage?.planName ?? usage?.planId ?? '—'}</strong>
          <span aria-hidden> · </span>
          {/*
            The window every figure above is counted over, stated rather than
            implied. "This month" and the billing period are the same thing only
            when a subscription happens to start on the first, and the tiles used
            to mix the two without saying which was which.
          */}
          対象期間{' '}
          <strong>
            {jaDate(usage?.periodStart)}〜{jaDate(usage?.periodEnd)}
          </strong>
          {isClinicAdmin && (
            <>
              <span aria-hidden> · </span>
              プランの変更はJMCへご依頼ください
            </>
          )}
        </p>
      </Card>

      {/*
        Shown whenever no single store is selected, including for a one-store
        clinic: with the allowance now per store, this table is where the actual
        remaining numbers live once the tiles stop showing one.
      */}
      {!store && stores.length > 0 && (
        <Card
          title="店舗ごとの状況"
          caption="記事と画像の上限は店舗ごとです。行を選ぶと、その店舗の設定と担当ユーザーを開きます。"
        >
          {pending ? (
            // Placeholders rather than the previous scope's rows. A revalidation
            // normally keeps its data on screen, but every figure here belongs to
            // the clinic being switched away from - see Skeleton.tsx.
            <SkeletonRows rows={3} columns={5} />
          ) : (
          <Table
            head={
              <>
                <th scope="col">店舗</th>
                <th scope="col" className="num">
                  今期
                </th>
                <th scope="col" className="num">
                  累計
                </th>
                <th scope="col" className="num">
                  記事の残り
                </th>
                <th scope="col">内訳</th>
                <th scope="col">最終作成</th>
              </>
            }
          >
            {stores.map((row) => (
              <StoreRow key={row.id} store={row} />
            ))}
          </Table>
          )}

          {/*
            The next page arrives when the reader reaches the bottom of this one.
            The button is the keyboard path, and what somebody presses when they
            want the rest now.
          */}
          {!pending && !storeList.done && (
            <div ref={storeList.sentinel} className="more-rows">
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                disabled={storeList.loading}
                onClick={storeList.more}
              >
                {storeList.loading ? '読み込み中…' : 'さらに表示'}
              </button>
              {storeList.error && (
                <span className="muted small" style={{ marginInlineStart: 8 }}>
                  読み込めませんでした。
                </span>
              )}
            </div>
          )}
        </Card>
      )}

      <Card
        title="最近の記事"
        caption={store ? `${store.name}の記事です。` : 'クリニック全体の最新の記事です。'}
        actions={
          recent.length > 0 && (
            <Link className="btn btn--sm btn--ghost" to="/articles">
              すべて見る
            </Link>
          )
        }
      >
        {pending ? (
          <SkeletonRows rows={4} columns={store ? 3 : 4} />
        ) : recent.length === 0 ? (
          <Empty>
            まだ記事がありません。「新規記事を作成」から、承認済みの情報をもとに下書きを作れます。
          </Empty>
        ) : (
          <>
            <Table
              head={
                <>
                  <th scope="col">タイトル</th>
                  {!store && <th scope="col">店舗</th>}
                  <th scope="col">状態</th>
                  <th scope="col">作成日</th>
                </>
              }
            >
              {recent.slice(0, RECENT_SHOWN).map((article) => (
                <tr key={article.id} className="row-link">
                  <td>
                    <Link className="row-link__target" to={`/articles/${article.id}`}>
                      {article.title}
                    </Link>
                  </td>
                  {!store && <td className="muted">{article.store_name}</td>}
                  <td>
                    <StatusPill status={article.status} />
                  </td>
                  <td>{jaDate(article.created_at)}</td>
                </tr>
              ))}
            </Table>
            {/*
              The server asks for one more than is shown, so "there are more"
              needs no count query - the extra row's existence is the answer.
            */}
            {recent.length > RECENT_SHOWN && (
              <p className="muted small" style={{ marginBottom: 0 }}>
                <Link to="/articles">ほかの記事もあります。一覧をすべて見る →</Link>
              </p>
            )}
          </>
        )}
      </Card>

      <div className="grid grid--2">
        {/*
          What replaced the marketing checklist. It said what the product does,
          to somebody already using it, in a space that could tell them whether
          they can use it right now.
        */}
        <Card
          title="生成に使える情報"
          caption="承認済みの情報だけがAIに渡ります。クリニック共通は全店舗で使われ、店舗の情報はその店舗だけで使われます。"
        >
          {pending ? (
            <SkeletonRows rows={5} columns={3} />
          ) : readiness.length === 0 ? (
            <Empty>まだ情報が登録されていません。下の「管理」から追加してください。</Empty>
          ) : (
            <div className="scopegroups">
              {groups.map((group) => (
                <FactScope
                  key={group.storeId ?? 'clinic'}
                  title={group.title}
                  note={group.note}
                  rows={group.rows}
                  blocking={group.blocking}
                />
              ))}
            </div>
          )}
        </Card>

        {/*
          Two audiences, two panels. A clinic admin manages the clinic; a store
          user works in one store and has nothing to manage - so they get what
          their store is set to write like, which is the thing that surprises
          them about their own output.
        */}
        {isClinicAdmin ? (
          <Card title="クリニックの管理" caption="設定と権限は、記事の内容に直接影響します。">
            <div className="quicklinks">
              <Link className="quicklink" to="/stores">
                <span className="quicklink__label">店舗と設定</span>
                <span className="quicklink__note">
                  サイトURL・トーン・WordPressカテゴリ。{jaNumber(stores.length)} 店舗
                </span>
              </Link>
              <Link className="quicklink" to="/members">
                <span className="quicklink__label">ユーザー管理</span>
                <span className="quicklink__note">担当店舗の割り当てと権限の停止</span>
              </Link>
              <Link className="quicklink" to="/personas">
                <span className="quicklink__label">ペルソナ</span>
                <span className="quicklink__note">記事の想定読者。AIで下書きできます</span>
              </Link>
              <Link className="quicklink" to="/articles?status=IN_REVIEW">
                <span className="quicklink__label">確認中の記事</span>
                <span className="quicklink__note">承認を待っている下書きだけを表示</span>
              </Link>
            </div>
          </Card>
        ) : (
          <Card
            title={store ? `${store.name}について` : 'あなたの担当'}
            caption="設定の変更はクリニック管理者に依頼してください。"
          >
            <div className="quicklinks">
              <Link className="quicklink" to="/articles/new">
                <span className="quicklink__label">新規記事を作成</span>
                <span className="quicklink__note">
                  承認済みの情報だけを使い、価格や医師名はAIが創作しません
                </span>
              </Link>
              <Link className="quicklink" to="/articles">
                <span className="quicklink__label">自分の店舗の記事</span>
                <span className="quicklink__note">下書きと確認状況の一覧</span>
              </Link>
              {store && (
                <Link className="quicklink" to={`/stores/${store.id}`}>
                  <span className="quicklink__label">店舗の設定を見る</span>
                  <span className="quicklink__note">
                    記事のトーンや掲載先。閲覧のみで、変更は管理者が行います
                  </span>
                </Link>
              )}
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
